import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "./supabase";

export const SP_TZ = "America/Sao_Paulo";

export const CATEGORIES = {
    jetski_passeio: "Passeio de jet ski",
    jetski_aluguel: "Aluguel de jet ski",
    lancha_passeio: "Passeio de lancha",
    lancha_compartilhada: "Lancha compartilhada",
    lancha_exclusiva: "Lancha exclusiva",
    escuna: "Passeio de escuna",
    drone: "Serviço de drone",
    outros: "Outras experiências",
};
export const PRICING = {
    per_person: "Por pessoa",
    per_equipment: "Por equipamento (ex.: por jet ski)",
    per_booking: "Por grupo (valor fechado)",
    per_boat: "Por embarcação inteira",
};
export const BOOKING_TYPES = {
    scheduled: "Data e horário definidos",
    flexible: "Exclusiva com data a combinar",
    whatsapp: "Solicitação pelo WhatsApp",
};
export const STATUS = {
    pending_review: "Solicitação recebida",
    awaiting_confirmation: "Aguardando confirmação",
    awaiting_payment: "Aguardando pagamento",
    payment_pending: "Pagamento pendente",
    deposit_paid: "Sinal pago",
    paid_full: "Pagamento integral confirmado",
    confirmed: "Reserva confirmada",
    completed: "Concluída",
    cancelled: "Cancelada",
    rescheduled: "Reagendada",
    expired: "Expirada",
    refund_pending: "Reembolso pendente",
    refunded: "Reembolsada",
};
export const MANUAL_STATUS = [
    "pending_review", "awaiting_confirmation", "awaiting_payment", "payment_pending",
    "confirmed", "completed", "cancelled", "expired", "refund_pending",
];
export const COUPON_MSG = {
    valid: "Cupom aplicado",
    invalid: "Cupom inválido",
    expired: "Cupom expirado",
    exhausted: "Cupom esgotado",
    not_eligible: "Cupom não válido para esta experiência",
    min_total: "Valor mínimo do cupom não atingido",
};
const OCCUPYING = ["awaiting_payment", "payment_pending", "deposit_paid", "paid_full", "confirmed", "rescheduled", "completed"];
export const occupies = (b) =>
    OCCUPYING.includes(b.status) && (!b.hold_expires_at || new Date(b.hold_expires_at) > new Date());

export const money = (n) =>
    `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const discountPct = (price, promo) =>
    price && promo && Number(promo) < Number(price) ? Math.round((1 - Number(promo) / Number(price)) * 100) : 0;
export const isUuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(String(v || ""));
export const tourPath = (t) => `/passeios/${t.slug || t.id}`;

const fmt = (opts) => new Intl.DateTimeFormat("pt-BR", { timeZone: SP_TZ, ...opts });
export const fmtTime = (v) => fmt({ hour: "2-digit", minute: "2-digit" }).format(new Date(v));
export const fmtDateTime = (v) => fmt({ dateStyle: "short", timeStyle: "short" }).format(new Date(v));
export const fmtLongDate = (v) => fmt({ weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date(v));
export const fmtDay = (d) => (d ? fmtLongDate(`${d}T12:00:00-03:00`) : "");
export const spDay = (v) => new Intl.DateTimeFormat("sv-SE", { timeZone: SP_TZ }).format(new Date(v));
export const todaySP = () => spDay(new Date());

export async function rpc(name, args) {
    if (!supabase) throw new Error("Supabase não configurado");
    const { data, error } = await supabase.rpc(name, args);
    if (error) throw error;
    return data;
}

export function useBookingSettings() {
    const q = useQuery({
        queryKey: ["booking-settings"],
        enabled: isSupabaseConfigured,
        retry: false,
        staleTime: 60_000,
        queryFn: async () => {
            const { data, error } = await supabase.from("booking_settings").select("*").eq("id", 1).maybeSingle();
            if (error) throw error;
            return data;
        },
    });
    return { deposit_pct: 30, hold_minutes: 5, online_payment_enabled: false, ...(q.data || {}) };
}

export function useAdminRows(key, build) {
    return useQuery({
        queryKey: ["admin", key],
        enabled: isSupabaseConfigured,
        retry: false,
        queryFn: async () => {
            const { data, error } = await build(supabase);
            if (error) throw error;
            return data || [];
        },
    });
}

export function useAdminRefresh() {
    const qc = useQueryClient();
    return () => qc.invalidateQueries({ queryKey: ["admin"] });
}

export const useAdminBookings = () =>
    useAdminRows("bookings", (sb) => sb.from("booking_requests").select("*").order("created_at", { ascending: false }).limit(1000));
export const useAdminSlots = () =>
    useAdminRows("slots", (sb) => sb.from("booking_slots").select("*").order("starts_at").limit(2000));
export const useAdminResources = () =>
    useAdminRows("resources", (sb) => sb.from("resources").select("*").order("name"));
export const useAdminPayments = () =>
    useAdminRows("payments", (sb) => sb.from("booking_payments").select("*").order("created_at", { ascending: false }).limit(2000));
export const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
export const bookingDay = (b, slotsById) =>
    b.slot_id && slotsById[b.slot_id] ? spDay(slotsById[b.slot_id].starts_at) : b.desired_date || "";

export function useQuote(payload, enabled) {
    const key = JSON.stringify(payload);
    const [state, setState] = useState({ quote: null, error: "", loading: false });
    useEffect(() => {
        if (!enabled || !supabase) {
            setState({ quote: null, error: "", loading: false });
            return undefined;
        }
        let live = true;
        setState((s) => ({ ...s, loading: true }));
        const t = setTimeout(async () => {
            try {
                const quote = await rpc("booking_quote", { p: JSON.parse(key) });
                if (live) setState({ quote, error: "", loading: false });
            } catch (e) {
                if (live) setState({ quote: null, error: e.message || "Não foi possível calcular", loading: false });
            }
        }, 350);
        return () => {
            live = false;
            clearTimeout(t);
        };
    }, [key, enabled]);
    return state;
}

export function bookingWaMessage({ tour, when, adults, kids, quote, code, name, notes }) {
    return [
        code ? `Olá! Enviei a solicitação de reserva ${code}.` : "Olá! Gostaria de solicitar uma reserva.",
        `Experiência: ${tour.title}`,
        when && `Data/horário: ${when}`,
        `Participantes: ${adults} adulto(s)${kids ? ` + ${kids} criança(s)` : ""}`,
        quote && `Valor calculado: ${money(quote.total)}`,
        name && `Nome: ${name}`,
        notes && `Observações: ${notes}`,
        "Aguardo a confirmação de disponibilidade.",
    ].filter(Boolean).join("\n");
}

export function downloadCsv(filename, rows) {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: filename });
    a.click();
    URL.revokeObjectURL(url);
}
